/**
 * Landing Page Builder V2 - Venue address helpers
 *
 * The venue block stores its address as a free-typed string (no place_id or
 * lat/lng). These helpers decide when that string is "real" enough to deep-link
 * into Google Maps, and build the deep-link URL.
 */

/** A real street address has at least this many whitespace-separated words (e.g. "123 Main Street"). */
const MIN_ADDRESS_WORDS = 3;

/**
 * Heuristic for whether a free-typed venue address looks like a real,
 * geocodable street address rather than placeholder text. Requires a digit plus
 * either a comma or at least {@link MIN_ADDRESS_WORDS} words, so "123 Main St,
 * City, ST" and the comma-less "10 Downing Street London SW1" both pass while
 * "TBD", "Main hall", or a bare "Building 5" do not. Used to decide whether the
 * public page makes the address clickable.
 *
 * @param address - The raw address string from the venue block (may be undefined).
 * @returns True when the address looks specific enough to link to Google Maps.
 */
export function isLikelyAddress(address?: string): boolean {
  const trimmed = address?.trim() ?? "";
  if (!trimmed || !/\d/.test(trimmed)) return false;
  const wordCount = trimmed.split(/\s+/).length;
  return trimmed.includes(",") || wordCount >= MIN_ADDRESS_WORDS;
}

/**
 * Builds a Google Maps search deep-link for a free-typed address. Callers should
 * gate on {@link isLikelyAddress} first so junk text is not linked.
 *
 * @param address - The address string to search for on Google Maps.
 * @returns A maps.google.com search URL with the address as the query.
 */
export function buildVenueMapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
}
